import {
    useState,
    useCallback,
    useEffect,
    useRef,
    Children,
    isValidElement,
} from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { Tooltip, TooltipTrigger } from '@/shared/ui/tooltip'
import { cn } from '@/true-fsd/shared/utils/css'
import { extractAlphaFromHex, convertToHexWithAlpha } from '../utils'
import { PRESET_COLORS, COLOR_PICKER } from '../constants'
import classes from './ColorPickerPopup.module.scss'

/**
 * Пропсы для компонента ColorPickerPopup
 */
export interface ColorPickerPopupProps {
    /**
     * Текущий цвет в формате hex с alpha (например, "#056FFF80")
     */
    value: string
    /**
     * Обработчик изменения цвета
     */
    onChange: (color: string) => void
    /**
     * Дополнительные CSS классы
     */
    className?: string
    /**
     * Дочерний элемент, который будет триггером попапа
     */
    children: React.ReactNode
    /**
     * Сохраненные кастомные цвета (максимум 4)
     */
    customColors?: string[]
    /**
     * Обработчик изменения сохраненных кастомных цветов
     */
    onCustomColorsChange?: (colors: string[]) => void
}

/**
 * Компонент попапа выбора цвета с поддержкой прозрачности
 * Использует react-colorful для выбора цвета и alpha канала
 */
export function ColorPickerPopup({
    value,
    onChange,
    className,
    children,
    customColors = [],
    onCustomColorsChange,
}: ColorPickerPopupProps) {
    // Состояние открытия попапа
    const [open, setOpen] = useState(false)

    // Состояние видимости тултипа
    // Скрываем тултип, когда попап открыт
    const [tooltipOpen, setTooltipOpen] = useState<boolean | undefined>(undefined)

    // Состояние preview цвета (из input, если валидный)
    const [previewColor, setPreviewColor] = useState<string | null>(null)

    // Валидность текущего hex кода
    const [isHexValid, setIsHexValid] = useState(false)

    // Ref для input с hex-кодом
    const hexInputRef = useRef<HTMLInputElement>(null)

    // Извлекаем hex и alpha из текущего значения
    const { hex, alpha } = extractAlphaFromHex(value)

    // Локальное состояние цвета для палитры (без alpha)
    const [localColor, setLocalColor] = useState(hex)

    // Локальное состояние alpha (0-1)
    const [localAlpha, setLocalAlpha] = useState(alpha)

    // Локальное состояние для input, чтобы разрешить ввод частичных значений
    const [hexInputValue, setHexInputValue] = useState(hex.toUpperCase())

    // Синхронизируем локальное состояние с пропсом value
    useEffect(() => {
        const extracted = extractAlphaFromHex(value)
        setLocalColor(extracted.hex)
        setLocalAlpha(extracted.alpha)
        // Обновляем input только если значение изменилось извне (не из input)
        setHexInputValue(extracted.hex.toUpperCase())
    }, [value])

    // Синхронизируем видимость тултипа с состоянием попапа
    // Когда попап открыт, тултип должен быть скрыт
    useEffect(() => {
        if (open) {
            setTooltipOpen(false)
        } else {
            // Когда попап закрыт, разрешаем тултипу показываться при наведении
            // Устанавливаем undefined, чтобы вернуться к автоматическому управлению
            setTooltipOpen(undefined)
            // Очищаем preview при закрытии попапа
            setPreviewColor(null)
            setIsHexValid(false)
        }
    }, [open])

    /**
     * Обработчик изменения цвета в палитре
     * Используется при изменении цвета через react-colorful (если будет добавлен)
     * Пока не используется, но оставлен для будущего расширения
     */
    const handleColorChange = useCallback(
        (newColor: string) => {
            setLocalColor(newColor)
            // Применяем текущий alpha к новому цвету
            const colorWithAlpha = convertToHexWithAlpha(newColor, localAlpha)
            onChange(colorWithAlpha)
        },
        [localAlpha, onChange]
    )

    /**
     * Обработчик изменения alpha канала
     */
    const handleAlphaChange = useCallback(
        (newAlpha: number) => {
            setLocalAlpha(newAlpha)
            // Применяем новый alpha к текущему цвету
            const colorWithAlpha = convertToHexWithAlpha(localColor, newAlpha)
            onChange(colorWithAlpha)
            // Если есть preview, обновляем его с новым alpha
            // Используем hex из preview, но применяем новый alpha
            if (previewColor && isHexValid) {
                const previewExtracted = extractAlphaFromHex(previewColor)
                const updatedPreview = convertToHexWithAlpha(
                    previewExtracted.hex,
                    newAlpha
                )
                setPreviewColor(updatedPreview)
            }
        },
        [localColor, onChange, previewColor, isHexValid]
    )

    /**
     * Обработчик выбора предустановленного цвета
     */
    const handlePresetColorClick = useCallback(
        (presetColor: string) => {
            const extracted = extractAlphaFromHex(presetColor)
            setLocalColor(extracted.hex)
            setLocalAlpha(extracted.alpha)
            onChange(presetColor)
            // Очищаем preview при выборе предустановленного цвета
            setPreviewColor(null)
            setIsHexValid(false)
        },
        [onChange]
    )

    /**
     * Обработчик клика на кнопку "+" для добавления кастомного цвета
     */
    const handleAddCustomColor = useCallback(() => {
        if (!previewColor || !onCustomColorsChange) {
            return
        }

        // Добавляем цвет в начало массива, ограничиваем до 4 элементов
        const updatedColors = [previewColor, ...customColors].slice(0, 4)
        onCustomColorsChange(updatedColors)
        // Очищаем preview после сохранения
        setPreviewColor(null)
        setIsHexValid(false)
    }, [previewColor, customColors, onCustomColorsChange])

    /**
     * Обработчик клика на кнопку кастомного цвета (11-я кнопка)
     * Фокусирует input с hex-кодом
     */
    const handleCustomColorClick = useCallback(() => {
        // Открываем попап, если он закрыт
        if (!open) {
            setOpen(true)
        }
        // Фокусируем input после небольшой задержки, чтобы попап успел открыться
        setTimeout(() => {
            hexInputRef.current?.focus()
            hexInputRef.current?.select()
        }, 100)
    }, [open])

    /**
     * Обработчик ввода hex значения вручную
     * Разрешает ввод частичных значений, но применяет цвет только при валидном формате
     */
    const handleHexInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value.trim()

            // Всегда обновляем значение в input для разрешения ввода
            // Приводим к верхнему регистру и ограничиваем длину
            const normalizedValue = inputValue.toUpperCase().slice(0, 7)
            setHexInputValue(normalizedValue)

            // Проверяем формат hex цвета (#RRGGBB или #RRGGBBAA)
            const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/
            if (hexPattern.test(inputValue)) {
                const extracted = extractAlphaFromHex(inputValue)
                setLocalColor(extracted.hex)
                setLocalAlpha(extracted.alpha)
                // Если введен цвет без alpha, используем текущий alpha
                const colorWithAlpha =
                    inputValue.length === 9
                        ? inputValue
                        : convertToHexWithAlpha(extracted.hex, localAlpha)
                onChange(colorWithAlpha)
                // Устанавливаем preview цвет и валидность
                setPreviewColor(colorWithAlpha)
                setIsHexValid(true)
            } else {
                // Если невалидный - убираем preview
                setPreviewColor(null)
                setIsHexValid(false)
            }
        },
        [localAlpha, onChange]
    )

    /**
     * Текущий hex цвет для отображения в инпуте
     * Используем локальное состояние для разрешения ввода частичных значений
     */
    const displayHex = hexInputValue

    /**
     * Проверяем, является ли текущий цвет одним из предустановленных
     */
    const isPresetColor = PRESET_COLORS.some(
        (preset) =>
            extractAlphaFromHex(preset).hex.toUpperCase() ===
            displayHex.toUpperCase()
    )

    /**
     * Цвет для 11-й кнопки (кастомный цвет)
     * Если текущий цвет не является предустановленным, показываем его
     * Иначе показываем прозрачный квадратик с плюсом
     */
    const customColorDisplay = isPresetColor ? 'transparent' : localColor

    // Извлекаем структуру Tooltip, если children - это Tooltip
    // В этом случае мы оборачиваем Popover внутри существующего TooltipTrigger
    let isTooltipWrapper = false
    let tooltipContent: React.ReactNode = null
    let tooltipTriggerChildren: React.ReactNode = null

    if (isValidElement(children) && children.type === Tooltip) {
        isTooltipWrapper = true
        const tooltipProps = children.props as { children?: React.ReactNode }
        const tooltipChildren = Children.toArray(tooltipProps.children)
        const tooltipTrigger = tooltipChildren.find(
            (child) =>
                isValidElement(child) &&
                child.props &&
                typeof child.props === 'object' &&
                ('asChild' in child.props || child.type === TooltipTrigger)
        )
        const tooltipContentElement = tooltipChildren.find(
            (child) =>
                isValidElement(child) &&
                child.type &&
                typeof child.type === 'object' &&
                'displayName' in child.type &&
                (child.type as { displayName?: string }).displayName ===
                    'TooltipContent'
        )
        if (tooltipTrigger && isValidElement(tooltipTrigger)) {
            const triggerProps = tooltipTrigger.props as {
                children?: React.ReactNode
            }
            tooltipTriggerChildren = triggerProps.children
        }
        if (tooltipContentElement && isValidElement(tooltipContentElement)) {
            tooltipContent = tooltipContentElement
        }
    }

    const popoverContent = (
        <PopoverContent
            className={cn(classes.popoverContent, className)}
            align="start"
            side="top"
            sideOffset={8}
            onPointerEnter={() => {
                // При наведении на попап, убеждаемся, что тултип скрыт
                if (open) {
                    setTooltipOpen(false)
                }
            }}
        >
            {/* Основная палитра */}
                <div className={classes.colorPickerContainer}>
                    {/* Предустановленные цвета (2 ряда по 5) */}
                    <div className={classes.presetColorsContainer}>
                        <div className={classes.presetColorsGrid}>
                            {PRESET_COLORS.map((presetColor, index) => {
                            const extracted = extractAlphaFromHex(presetColor)
                                const isSelected =
                                extractAlphaFromHex(value).hex.toUpperCase() ===
                                    extracted.hex.toUpperCase()
                                const isWhite =
                                    extracted.hex.toUpperCase() === '#FFFFFF'

                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        className={cn(
                                            classes.presetColorButton,
                                            {
                                                [classes.presetColorButtonSelected]:
                                                    isSelected,
                                            },
                                            {
                                                [classes.presetColorButtonWhite]:
                                                    isWhite,
                                            }
                                        )}
                                        style={{
                                            backgroundColor: extracted.hex,
                                        }}
                                        onClick={() =>
                                            handlePresetColorClick(presetColor)
                                        }
                                        aria-label={`Выбрать цвет ${extracted.hex}`}
                                    />
                                )
                            })}

                            {/* Кнопка "+" для добавления кастомного цвета */}
                            <button
                                type="button"
                                className={cn(
                                    classes.presetColorButton,
                                    classes.presetColorButtonCustom,
                                    {
                                        [classes.presetColorButtonActive]:
                                            isHexValid,
                                    }
                                )}
                                style={{
                                    backgroundColor: 'transparent',
                                }}
                                onClick={handleAddCustomColor}
                                disabled={!isHexValid}
                                aria-label="Добавить кастомный цвет"
                            >
                                <svg
                                    width="7"
                                    height="7"
                                    viewBox="0 0 7 7"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={classes.customColorIcon}
                                >
                                    <path
                                        d="M3.49561 0.5V6.5"
                                        stroke="#E9E9ED"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M6.5 3.49561L0.5 3.49561"
                                        stroke="#E9E9ED"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>

                            {/* Слоты для сохраненных кастомных цветов (максимум 4) */}
                            {/* Слот 1: preview или первый сохраненный цвет */}
                            {(() => {
                                const slot1Color = previewColor || customColors[0]
                                if (!slot1Color) return null
                                const extracted = extractAlphaFromHex(slot1Color)
                                const isSelected =
                                    extractAlphaFromHex(value).hex.toUpperCase() ===
                                    extracted.hex.toUpperCase()
                                const isWhite =
                                    extracted.hex.toUpperCase() === '#FFFFFF'

                                return (
                                    <button
                                        key="custom-0"
                                        type="button"
                                        className={cn(classes.presetColorButton, {
                                            [classes.presetColorButtonSelected]:
                                                isSelected,
                                            [classes.presetColorButtonWhite]: isWhite,
                                            [classes.presetColorButtonPreview]:
                                                previewColor !== null,
                                        })}
                                        style={{
                                            backgroundColor: extracted.hex,
                                        }}
                                        onClick={() => {
                                            if (previewColor) {
                                                // Если это preview - применяем цвет
                                                onChange(previewColor)
                                            } else {
                                                // Если это сохраненный цвет - применяем его
                                                onChange(slot1Color)
                                                // Очищаем preview при применении сохраненного цвета
                                                setPreviewColor(null)
                                                setIsHexValid(false)
                                            }
                                        }}
                                        aria-label={`Выбрать цвет ${extracted.hex}`}
                                    />
                                )
                            })()}

                            {/* Слоты 2-4: остальные сохраненные цвета */}
                            {/* Если есть preview, показываем customColors[0..2], иначе customColors[1..3] */}
                            {customColors
                                .slice(previewColor ? 0 : 1, previewColor ? 3 : 4)
                                .map((color, index) => {
                                const extracted = extractAlphaFromHex(color)
                                const isSelected =
                                    extractAlphaFromHex(value).hex.toUpperCase() ===
                                    extracted.hex.toUpperCase()
                                const isWhite =
                                    extracted.hex.toUpperCase() === '#FFFFFF'

                                return (
                                    <button
                                        key={`custom-${index + 1}`}
                                        type="button"
                                        className={cn(classes.presetColorButton, {
                                            [classes.presetColorButtonSelected]:
                                                isSelected,
                                            [classes.presetColorButtonWhite]: isWhite,
                                        })}
                                        style={{
                                            backgroundColor: extracted.hex,
                                        }}
                                        onClick={() => {
                                            onChange(color)
                                            // Очищаем preview при применении сохраненного цвета
                                            setPreviewColor(null)
                                            setIsHexValid(false)
                                        }}
                                        aria-label={`Выбрать цвет ${extracted.hex}`}
                                    />
                                )
                            })}
                        </div>
                    </div>

                    {/* Полоска прозрачности (4-й ряд) */}
                    <div className={classes.alphaSliderContainer}>
                        <div
                            className={classes.alphaSliderTrack}
                            style={{
                            // Накладываем градиент цвета поверх шахматного паттерна
                            backgroundImage: `
                                linear-gradient(to right, ${localColor}00, ${localColor}FF),
                                linear-gradient(45deg, #d0d0d0 25%, transparent 25%),
                                linear-gradient(-45deg, #d0d0d0 25%, transparent 25%),
                                linear-gradient(45deg, transparent 75%, #d0d0d0 75%),
                                linear-gradient(-45deg, transparent 75%, #d0d0d0 75%)
                            `,
                            backgroundSize:
                                '100% 100%, 8px 8px, 8px 8px, 8px 8px, 8px 8px',
                            backgroundPosition:
                                '0 0, 0 0, 0 4px, 4px -4px, -4px 0px',
                            }}
                        >
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={localAlpha}
                                onChange={(e) =>
                                handleAlphaChange(parseFloat(e.target.value))
                                }
                                className={classes.alphaSlider}
                                aria-label="Прозрачность"
                            />
                        </div>
                    </div>

                    {/* Инпут для ввода hex значения (5-й ряд) */}
                    <div className={classes.hexInputContainer}>
                        <input
                        ref={hexInputRef}
                            id="color-hex-input"
                            type="text"
                            className={classes.hexInput}
                            value={displayHex}
                            onChange={handleHexInputChange}
                            placeholder="#000000"
                            maxLength={7}
                        />
                    </div>
                </div>
        </PopoverContent>
    )

    // Если был передан Tooltip, используем правильную структуру для избежания вложенности button в button
    // Проблема: TooltipTrigger создает button, PopoverTrigger asChild на button создает вложенность
    // Решение: Используем PopoverAnchor для позиционирования Popover, а PopoverTrigger помещаем вне TooltipTrigger
    // Структура: Tooltip -> TooltipTrigger asChild на button -> внутри Popover -> PopoverAnchor + PopoverTrigger вне TooltipTrigger
    // Но это сложно. Проще: Tooltip -> TooltipTrigger asChild на div -> Popover -> PopoverTrigger asChild на button
    if (isTooltipWrapper && tooltipContent && tooltipTriggerChildren) {
        // Важно: Popover должен быть непосредственным родителем PopoverTrigger
        // Поэтому структура: span (для TooltipTrigger asChild) -> Popover -> PopoverTrigger asChild -> button
        // Управляем видимостью тултипа через проп open, чтобы скрывать его при открытом попапе
        return (
            <Tooltip delayDuration={300} open={tooltipOpen}>
                <TooltipTrigger asChild>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            lineHeight: 0,
                        }}
                        role="presentation"
                    >
                        <Popover open={open} onOpenChange={setOpen}>
                            <PopoverTrigger asChild>
                                {tooltipTriggerChildren}
                            </PopoverTrigger>
                            {popoverContent}
                        </Popover>
                    </span>
                </TooltipTrigger>
                {tooltipContent}
            </Tooltip>
        )
    }

    // Если Tooltip не передан, просто используем Popover
    const triggerElement: React.ReactNode = isTooltipWrapper
        ? tooltipTriggerChildren
        : children
    
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{triggerElement}</PopoverTrigger>
            {popoverContent}
        </Popover>
    )
}
